// 新規コメントの入力ウィンドウ（添付イメージ 1 相当）。
// place-comment でキャンバスをクリックした位置に表示し、本文を入力して ENTER で確定すると
// その位置へコメントスレッド（ピン）を作成する。SHIFT+ENTER は改行。Escape / 外側クリックで取消。
//
// ビューポート固定サイズで描画するため、アンカー（クリック位置）から射影した screen 座標へ配置する。
// 投稿後のコメントピン（comment-pin.tsx）とアンカーの「位置」と「カタチ」を揃える:
// - 位置: ピンと同じく、ボックスの尖った角をアンカー（クリック位置）にぴったり合わせる。
//   clamp で box だけずらす方式は採らない（入力中の尖り角と投稿後のピンの位置が食い違い、
//   送信した瞬間にピンが横へ跳んで見えるため）。
// - カタチ: ピンと同じく、アンカー側の角だけ尖らせた角丸（吹き出しの尾）でアンカーを指す。
// これにより、入力中のウィンドウと投稿後に残るピンが同じ場所・同じ形で連続して見える。
// 画面端では開く向きを反転する（上に収まらなければ下へ、右に収まらなければ左へ）。
// 反転時も「尖った角＝アンカー」は維持し、尖らせる角だけを鏡映しにする。

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { CommentAvatar } from "./comment-avatar";
import { CommentComposer } from "./comment-composer";
import type { CommentImageUploader } from "./comment-image-upload";

interface Props {
  /** アンカー（尖り角）の screen 座標。 */
  readonly screenX: number;
  readonly screenY: number;
  readonly authorName: string;
  readonly authorAvatar?: string | undefined;
  /** 本文と添付画像 URL（公開 URL）で起点コメントを確定する。 */
  readonly onSubmit: (body: string, images: readonly string[]) => void;
  readonly onCancel: () => void;
  /** 画像貼り付けを有効にする場合のアップローダ（参照専用等では未指定）。 */
  readonly imageUploader?: CommentImageUploader | undefined;
  /** 送信確定処理中かどうかの変化を親へ通知する（送信中は入力ウィンドウを閉じないため）。 */
  readonly onSubmittingChange?: ((submitting: boolean) => void) | undefined;
}

const WINDOW_WIDTH = 300;
const VIEWPORT_MARGIN = 8;

interface Pos {
  /** 上下どちらへ開くか（above=アンカーの上側に開く。既定）。 */
  readonly placement: "above" | "below";
  /** 左右どちらへ開くか（expand-right=アンカーから右へ広がる。既定）。 */
  readonly align: "expand-right" | "expand-left" | "clamp";
  /** align=clamp（極端に狭いビューポートのフォールバック）のときだけ使う左端座標。 */
  readonly clampedLeft: number;
}

export function CommentInputWindow({
  screenX,
  screenY,
  authorName,
  authorAvatar,
  onSubmit,
  onCancel,
  imageUploader,
  onSubmittingChange,
}: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  // 既定はピンと同じ「アンカーの右上側」に開く。収まらないときだけ向きを反転する。
  const [pos, setPos] = useState<Pos>({
    placement: "above",
    align: "expand-right",
    clampedLeft: screenX,
  });

  // 画面内へ収める配置を決める。アンカー（尖り角）は動かさず、開く「向き」で調整する。
  // - 上下: 上側に高さが収まらなければ下側へ開く（尖り角は上下反転）。
  // - 左右: 右側に幅が収まらなければ左側へ開く（尖り角は左右反転）。左右どちらにも収まらない
  //   極端に狭いビューポートでのみ、フォールバックとして左右 clamp する（アンカー一致より
  //   操作可能性を優先。通常の画面幅では発生しない）。
  // 入力で行数が増えてボックスが伸びると上側表示では上端からはみ出し得るため、
  // ResizeObserver で自身のサイズ変化を監視し、そのたびに配置を再評価する。
  useLayoutEffect(() => {
    const el = ref.current;
    if (typeof window === "undefined") return undefined;
    const recompute = (): void => {
      const width = el?.offsetWidth ?? WINDOW_WIDTH;
      const height = el?.offsetHeight ?? 0;
      const fitsRight = screenX + width + VIEWPORT_MARGIN <= window.innerWidth;
      const fitsLeft = screenX - width >= VIEWPORT_MARGIN;
      const align: Pos["align"] = fitsRight ? "expand-right" : fitsLeft ? "expand-left" : "clamp";
      const placement: Pos["placement"] =
        screenY - height >= VIEWPORT_MARGIN ? "above" : "below";
      const clampedLeft =
        align === "clamp"
          ? clamp(screenX, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN))
          : screenX;
      setPos((prev) =>
        prev.placement === placement && prev.align === align && prev.clampedLeft === clampedLeft
          ? prev
          : { placement, align, clampedLeft }
      );
    };
    recompute();
    // jsdom 等 ResizeObserver 非対応環境では初回配置のみ（サイズ変化の追従はスキップ）。
    if (el && typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(recompute);
      observer.observe(el);
      return () => observer.disconnect();
    }
    return undefined;
  }, [screenX, screenY]);

  // アンカー側の尖り角（4px）の位置: 上開き×右広がり=左下 / 上開き×左広がり=右下 /
  // 下開き×右広がり=左上 / 下開き×左広がり=右上（border-radius は 左上 右上 右下 左下 の順）。
  const expandLeft = pos.align === "expand-left";
  const borderRadius =
    pos.placement === "above"
      ? expandLeft
        ? "18px 18px 4px 18px"
        : "18px 18px 18px 4px"
      : expandLeft
        ? "18px 4px 18px 18px"
        : "4px 18px 18px 18px";
  // 尖り角をアンカーへ合わせる平行移動（ピンの translateY(-100%) と同じ方式）。
  const translateX = expandLeft ? "-100%" : "0";
  const translateY = pos.placement === "above" ? "-100%" : "0";

  const style: CSSProperties = {
    position: "fixed",
    left: `${pos.align === "clamp" ? pos.clampedLeft : screenX}px`,
    top: `${screenY}px`,
    width: `${WINDOW_WIDTH}px`,
    background: "var(--color-surface, #ffffff)",
    // 投稿後のピン（comment-pin.tsx の borderRadius: "18px 18px 18px 4px"）とカタチを揃える。
    borderRadius,
    transform: `translate(${translateX}, ${translateY})`,
    boxShadow: "0 8px 28px rgba(0,0,0,0.24)",
    padding: "8px 10px",
    display: "flex",
    alignItems: "flex-end",
    gap: "8px",
    pointerEvents: "auto",
  };

  return (
    <div
      ref={ref}
      style={style}
      data-testid="comment-input-window"
      data-comment-ui=""
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <CommentAvatar name={authorName} src={authorAvatar} size={28} isSelf />
      <div style={{ flex: "1 1 auto" }}>
        <CommentComposer
          placeholder="コメントを追加"
          autoFocus
          clearOnSubmit={false}
          onSubmit={onSubmit}
          onCancel={onCancel}
          imageUploader={imageUploader}
          onSubmittingChange={onSubmittingChange}
        />
      </div>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
